<template>
  <div>
    <PageTitle :title="$t('global.system-status')" />
    <LoadingState v-if="!statusPageSummary" />
    <div v-else-if="statusPageSummary" class="row status-page">
      <div class="col-lg-6">
        <div class="toast">
          <div
            v-if="statusPageSummary.status.indicator === 'none'"
            class="toast-header toast-header-variant-success"
          >
            <Icon icon="success" :size="20" class="toast-icon" color="sigma" />
            {{ statusPageSummary.status.description }}
          </div>
          <div v-else class="toast-header toast-header-variant-error">
            <Icon icon="error" :size="20" class="toast-icon" color="tau" />
            {{ statusPageSummary.status.description }}
          </div>
        </div>
        <Card :title="$t('global.status')" :height100="false">
          <template>
            <CardCollapse
              v-for="(group, groupIndex) in statusPageSummary.components.filter(
                (item) => item.group
              )"
              :key="groupIndex"
              :title="group.name"
            >
              <ul>
                <li
                  v-for="(component, index) in statusPageSummary.components.filter((item) =>
                    group.components.includes(item.id)
                  )"
                  :key="index"
                >
                  {{ component.name }}
                  <Icon
                    v-if="component.status === 'operational'"
                    icon="success"
                    :size="14"
                    class="toast-icon float-right"
                    color="sigma"
                  />
                  <Icon
                    v-if="component.status !== 'operational'"
                    icon="error"
                    :size="14"
                    class="toast-icon float-right"
                    color="tau"
                  />
                </li>
              </ul>
            </CardCollapse>
          </template>
        </Card>
      </div>
      <div class="col-lg-6">
        <Card :title="$t('global.past-incidents')" :height100="false">
          <template v-for="(day, i) in statusPageIncidentsCalendar">
            <div :key="i">
              <h6 class="mb-2">
                {{ day.date }}
              </h6>
              <ul v-if="day.incidents.length" class="white-space-content pl-0">
                <li v-for="(incident, j) in day.incidents" :key="j" class="mb-1">
                  <a :href="incident.link" target="_blank">{{ incident.name }}</a>
                </li>
              </ul>
              <div v-else class="white-space-content text-black-50">
                {{ $t("global.no-incidents") }}
              </div>
              <hr />
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import { getStatusPageIncidents, getStatusPageSummary } from "@/utils/statusPage";
import { onMounted, ref } from "@vue/composition-api";
import Card from "@/components/display/Card";
import CardCollapse from "@/components/display/card-sub-components/CardCollapse";
import { dateFormat } from "leatherman-js/utils/formatters/time";
import Icon from "@/components/display/icon/Icon";
import LoadingState from "@/components/display/LoadingState";
import PageTitle from "@/components/display/PageTitle";

export default {
  name: "SystemStatus",
  props: {
    incidentDays: {
      type: Number,
      default: 14,
    },
  },
  components: {
    Card,
    CardCollapse,
    Icon,
    LoadingState,
    PageTitle,
  },
  setup(props) {
    const statusPageSummary = ref(null);
    const statusPageIncidentsCalendar = ref(null);

    onMounted(async () => {
      await getStatusPageSummary().then((summary) => {
        statusPageSummary.value = summary;
      });
      await getStatusPageIncidents().then((incidents) => {
        statusPageIncidentsCalendar.value = buildIncidentsCalendar(incidents.incidents);
      });
    });

    const buildIncidentsCalendar = (incidents) => {
      const calendar = [];
      let currentDate = new Date();

      for (let i = 0; i < props.incidentDays; i++) {
        const date = new Date();
        date.setDate(currentDate.getDate() - i);
        date.setHours(0, 0, 0, 0);

        calendar.push({
          date: dateFormat(date.getTime() / 1000),
          incidents: incidents
            .filter((item) => {
              const incidentDate = new Date(item.created_at);
              incidentDate.setHours(0, 0, 0, 0);
              return incidentDate.getTime() === date.getTime();
            })
            .map((item) => ({
              name: item.name,
              link: item.shortlink,
            })),
        });
      }

      return calendar;
    };

    return {
      statusPageSummary,
      statusPageIncidentsCalendar,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables.scss";
.status-page {
  ul {
    list-style-type: none;
    padding-left: 1rem;
    li {
      margin-bottom: 1rem;
    }
  }
  .toast {
    max-width: 100%;
    opacity: 1;
    box-shadow: none;

    .toast-header {
      padding: 0.875rem 0.875rem;
      border-left-width: 4px;
      border-left-style: solid;
    }
    .toast-icon {
      margin-right: 1.2rem;
    }
  }
}
</style>
